import { ArrowUpRight, Building2, Warehouse, Fence, Flame, Wrench, Hammer, ShieldCheck } from "lucide-react";
import { useContent } from "@/context/ContentContext";

const ICONS = {
  building: Building2,
  warehouse: Warehouse,
  fence: Fence,
  flame: Flame,
  wrench: Wrench,
  hammer: Hammer,
  shield: ShieldCheck,
};

export default function Services() {
  const { content } = useContent();
  const services = content?.services || [];
  const sections = content?.sections || {};
  const subtitle = sections.services_subtitle ||
    "Eight years of construction and welding experience — specializing in metal buildings and delivering everything from small carports to large-scale custom steel and concrete structures.";

  const scrollContact = () => document.getElementById("contact")?.scrollIntoView({ behavior: "smooth" });

  return (
    <section id="services" data-testid="services-section" className="py-24 md:py-32 bg-[#F5F7FA]">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-16">
          <div>
            <div className="font-display tracking-[0.3em] text-xs text-[#4AB300] mb-4">WHAT WE BUILD</div>
            <h2 className="font-display uppercase text-4xl sm:text-5xl md:text-6xl leading-none tracking-tight">
              Services<br />
              <span className="text-[#9CA3AF]">Metal · Steel · Concrete · Custom</span>
            </h2>
          </div>
          <p data-testid="services-subtitle" className="text-[#374151] max-w-md text-base md:text-lg leading-relaxed">
            {subtitle}
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4">
          {services.map((s, i) => {
            const Icon = ICONS[s.icon_key] || Building2;
            const img = s.image_data || s.image_url;
            return (
              <div
                key={s.id || s.title}
                data-testid={`service-card-${s.key || s.id || i}`}
                className="group relative border border-[#E5E7EB] -ml-px -mt-px bg-white hover:bg-[#EAEDF2] transition-colors cursor-pointer overflow-hidden"
                onClick={scrollContact}
              >
                <div className="aspect-[4/3] overflow-hidden relative bg-[#EAEDF2]">
                  {img && (
                    <img
                      src={img}
                      alt={s.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                  )}
                  <div className="absolute top-4 left-4 w-10 h-10 bg-[#84FB00] flex items-center justify-center">
                    <Icon size={20} className="text-[#0A0E1A]" />
                  </div>
                  <div className="absolute top-4 right-4 font-display text-[#6B7280] text-sm tracking-widest">
                    0{i + 1}
                  </div>
                </div>
                <div className="p-6">
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="font-display text-2xl uppercase tracking-wide text-[#0A0E1A]">{s.title}</h3>
                    <ArrowUpRight size={20} className="text-[#4AB300] group-hover:rotate-45 transition-transform" />
                  </div>
                  <p className="text-sm text-[#4B5563] leading-relaxed">{s.description}</p>
                </div>
              </div>
            );
          })}
        </div>

        <div className="mt-8 border border-[#E5E7EB] bg-white p-8 flex flex-col md:flex-row md:items-center justify-between gap-6">
          <div>
            <div className="font-display uppercase text-2xl tracking-wide">And Much More</div>
            <p className="text-[#4B5563] mt-2">Have a custom project in mind? Tell us all about it!</p>
          </div>
          <button
            data-testid="services-cta-button"
            onClick={scrollContact}
            className="bg-[#84FB00] hover:bg-[#6EE500] transition-colors px-8 py-4 font-display tracking-widest uppercase text-[#0A0E1A] self-start md:self-auto"
          >
            Request Free Estimate
          </button>
        </div>
      </div>
    </section>
  );
}
